// Library Methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import TransferDeviceModal from "../../TransferDeviceModal/TransferDeviceModal";
import { EllipsisIcon, TransferIcon } from "src/assets/icons";

// Styles
import {
  getSurfaceTokens,
  getPopoverPaperSx,
} from "src/styles/mui/muiStylesHelper";

// Types
import { DeviceType } from "src/services/devices/types";

// Services
import { useTransferDevice } from "src/services/devices/hooks/useTransferDevice";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

const IN_PROGRESS_STAGES = ["Exam", "StartingExam", "SubmittingExam"];

type DeviceCardMenuType = {
  device: DeviceType;
  isLinked: boolean;
};

const DeviceCardMenu = ({ device, isLinked }: DeviceCardMenuType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const { clinicInfo, allClinicInfo } = useClinicContext();

  const { mutateAsync: transferDeviceMutate } = useTransferDevice(
    clinicInfo?.id
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openTransferModal, setOpenTransferModal] = useState(false);

  const hasMultipleClinics = useMemo(
    () => (allClinicInfo?.length ?? 0) > 1,
    [allClinicInfo?.length]
  );

  const isExamInProgress = useMemo(
    () =>
      IN_PROGRESS_STAGES.includes(device?.deviceStatus?.stage?.stageType ?? ""),
    [device?.deviceStatus?.stage?.stageType]
  );

  const blockedReason = useMemo(() => {
    if (isExamInProgress)
      return t("The device is running an exam and cannot be transferred.");
    if (isLinked)
      return t("Unlink the exam from this device before transferring it.");
    return null;
  }, [isExamInProgress, isLinked, t]);

  const deviceInfoRows = useMemo(
    () => [
      {
        label: t("IP Address"),
        value: device?.deviceStatus?.ipAddress ?? "N/A",
      },
      {
        label: t("MAC Address"),
        value: device?.deviceStatus?.macAddress ?? "N/A",
      },
      {
        label: t("Randomized MAC Address"),
        value: device?.deviceStatus?.randomizedMACAddress ?? "N/A",
      },
    ],
    [
      device?.deviceStatus?.ipAddress,
      device?.deviceStatus?.macAddress,
      device?.deviceStatus?.randomizedMACAddress,
      t,
    ]
  );

  const handleTransfer = useCallback(
    async (targetClinicId: string) => {
      await transferDeviceMutate(
        { deviceId: device?.id, targetClinicId },
        { onSuccess: () => setOpenTransferModal(false) }
      );
    },
    [device?.id, transferDeviceMutate]
  );

  return (
    <Box display="flex">
      <Tooltip title={t("Device Options")}>
        <span>
          <IconButton
            sx={{ padding: "0px", color: onSurface }}
            aria-label="device-options-button"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <EllipsisIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ sx: { py: "6px" } }}
        PaperProps={{
          sx: {
            ...getPopoverPaperSx(theme),
            "& .MuiMenuItem-root": { fontSize: "14px", minHeight: "36px" },
            "& .MuiMenuItem-root:hover": {
              backgroundColor: "#5B6D7A20",
            },
          },
        }}
      >
        <Box sx={{ px: "16px", py: "4px" }} aria-label="device-info-section">
          {deviceInfoRows.map((row) => (
            <Typography key={row.label} fontSize="12px" color={mutedText}>
              {`${row.label}: ${row.value}`}
            </Typography>
          ))}
        </Box>

        {hasMultipleClinics && <Divider sx={{ my: "6px" }} />}

        {hasMultipleClinics && (
          <Tooltip title={blockedReason ?? ""} placement="right">
            <span>
              <MenuItem
                disabled={Boolean(blockedReason)}
                aria-label="transfer-device-menu-item"
                onClick={() => {
                  setAnchorEl(null);
                  setOpenTransferModal(true);
                }}
                sx={{
                  color: theme.palette.mode === "dark" ? "#FFF" : undefined,
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: "28px !important", color: onSurface }}
                >
                  <TransferIcon width="18px" height="18px" />
                </ListItemIcon>
                {t("Transfer Device")}
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      {hasMultipleClinics && (
        <TransferDeviceModal
          open={openTransferModal}
          deviceAlias={device?.alias}
          onClose={() => setOpenTransferModal(false)}
          onConfirm={handleTransfer}
        />
      )}
    </Box>
  );
};

export default DeviceCardMenu;
